import React from 'react';
import { Box } from '@mui/material';
import { Link as JoyLink, Typography } from '@mui/joy';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import _ from 'lodash';
import { SkeletonListRows } from './HeroSkeletons';
import { heroSectionSx } from './heroCardStyles';
import HeroContactList from './HeroContactList';
import { InstalledBaseNodeContactPersonList } from '../../graphql/queries/installedBaseNodeContactPersonList';
import { pickSummaryContacts } from '../../utils/contactAssignments';
import useNodeContactTabLink from '../../hooks/useNodeContactTabLink';

// Beyond this the card summarises ("+ N more contacts") and links to the
// node's contact tab — same cap as the service center card next to it.
const MAX_CONTACTS = 2;
// Same variables as the node contact tab (InstalledBaseNodeContacts) so both
// share one cache entry and the tab's refetchQueries keep the card current.
const LIST_LIMIT = 100;

interface Props {
    /** The asset's installedBaseNodeId, or the systemId (systems are nodes). */
    nodeId: string;
}

/**
 * The customer's contact persons for this machine (IBM-1086) — the contacts
 * assigned to the node, inherited ones included, directly assigned first.
 * Rendered by HeroCustomerCard for internal viewers only, as a plain section
 * below the customer's address.
 */
const HeroCustomerContacts = ({ nodeId }: Props) => {
    const { t } = useTranslation();

    const listQuery = useQuery(InstalledBaseNodeContactPersonList, {
        variables: {
            nodeId,
            filter: { includeInherited: true },
            pagination: { start: 0, limit: LIST_LIMIT },
        },
        fetchPolicy: 'cache-and-network',
    });
    const contactTab = useNodeContactTabLink(nodeId);

    const contactList = _.get(listQuery.data, 'installedBaseNode.contactPersonList');
    const { visible, hiddenCount } = pickSummaryContacts(contactList?.assignments, MAX_CONTACTS, contactList?.total);

    let content: React.ReactNode;
    if (listQuery.loading && !contactList) {
        content = <SkeletonListRows rows={2} variant="text" />;
    } else if (visible.length === 0) {
        content = (
            <Typography level="bodySmall" sx={{ color: '#777' }} data-testid="customer-contacts-empty">
                {t('No contact person assigned')}
                {contactTab.canAssign && contactTab.url && (
                    <>
                        {' · '}
                        <JoyLink component={RouterLink} to={contactTab.url} data-testid="customer-contacts-assign">
                            {t('Assign')}
                        </JoyLink>
                    </>
                )}
            </Typography>
        );
    } else {
        content = (
            <HeroContactList
                contacts={visible.map(({ contactPerson }) => contactPerson)}
                hiddenCount={hiddenCount}
                moreTo={contactTab.url}
                contactTestId="customer-contact"
            />
        );
    }

    return (
        <Box sx={heroSectionSx} data-testid="customer-contacts">
            {content}
        </Box>
    );
};

export default HeroCustomerContacts;
